import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { Bold, Italic, List, ListOrdered, Quote, Heading2, Heading3, Link2, Undo2, Redo2, Pilcrow } from "lucide-react";

/**
 * Rich-text editor on TipTap. Value/onChange use HTML strings.
 * Output is a small, safe subset: <p>, <strong>, <em>, <h2>, <h3>, <ul>, <ol>, <li>, <blockquote>, <a>.
 */
export default function RichTextEditor({ value, onChange, placeholder, testid }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Link.configure({ openOnClick: false, autolink: false, HTMLAttributes: { rel: "noopener nofollow", target: "_blank" } }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "prose prose-jade max-w-none min-h-[300px] focus:outline-none px-4 py-4 text-[15px] leading-[1.85] font-light text-ink/85",
        "data-testid": testid || "rich-text",
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html === "<p></p>" ? "" : html);
    },
  });

  // Sync external value changes (e.g. when loading a different post)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") !== current && (value || "") !== "") {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const Btn = ({ active, onClick, title, children, dt }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      data-testid={dt}
      className={`p-2 hover:bg-ivory transition-colors border border-transparent ${active ? "bg-jade/10 text-jade border-jade/30" : "text-ink/70"}`}
    >
      {children}
    </button>
  );

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (leave blank to remove):", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
  };

  return (
    <div className="border border-mist bg-ivory">
      <div className="flex flex-wrap items-center gap-1 border-b border-mist px-2 py-2">
        <Btn dt="rte-paragraph" title="Paragraph" onClick={() => editor.chain().focus().setParagraph().run()} active={editor.isActive("paragraph")}><Pilcrow size={15} /></Btn>
        <Btn dt="rte-h2" title="Heading 2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })}><Heading2 size={15} /></Btn>
        <Btn dt="rte-h3" title="Heading 3" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })}><Heading3 size={15} /></Btn>
        <span className="w-px h-5 bg-mist mx-1" />
        <Btn dt="rte-bold" title="Bold" onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")}><Bold size={15} /></Btn>
        <Btn dt="rte-italic" title="Italic" onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")}><Italic size={15} /></Btn>
        <Btn dt="rte-link" title="Link" onClick={setLink} active={editor.isActive("link")}><Link2 size={15} /></Btn>
        <span className="w-px h-5 bg-mist mx-1" />
        <Btn dt="rte-bullet" title="Bullet list" onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")}><List size={15} /></Btn>
        <Btn dt="rte-ordered" title="Numbered list" onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")}><ListOrdered size={15} /></Btn>
        <Btn dt="rte-quote" title="Quote" onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")}><Quote size={15} /></Btn>
        <span className="w-px h-5 bg-mist mx-1" />
        <Btn dt="rte-undo" title="Undo" onClick={() => editor.chain().focus().undo().run()}><Undo2 size={15} /></Btn>
        <Btn dt="rte-redo" title="Redo" onClick={() => editor.chain().focus().redo().run()}><Redo2 size={15} /></Btn>
      </div>
      <EditorContent editor={editor} placeholder={placeholder} />
    </div>
  );
}
